import { createHOC } from 'vue-hoc';
import { omit, merge, keyBy, cloneDeep, chunk, concat } from 'lodash';
import Fuse from 'fuse.js';
import Promise from 'bluebird';

import withUser from '../../../../lib/common/hoc/withUser';

import fetchGroupData from '~lib/common/utils/fetchGroupData';

import GroupsList from './GroupsList';
import { stripPrefix } from '../../common/GroupName';
import auth from '../../../../lib/common/providers/auth';
import userManagement from '../../../../lib/common/providers/userManagement';

function parseGroupMetadata(group) {
  try {
    return JSON.parse(group.description);
  } catch (e) {
    if (e.name === 'SyntaxError') {
      return {};
    }
    throw e;
  }
}

const options = {
  name: 'GroupsListContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
    parentGroupId: {
      type: String,
    },
  },
  data() {
    return {
      state: {
        inviting: false,
        loading: false,
        updating: false,
        removing: false,
        groups: [],
        allGroups: [],
        error: null,
        keyOwner: null,
      },
      fuse: null,
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
    try {
      this.state.loading = true;
      const userRole = this.user.role;
      const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
      if (this.parentGroupId) {
        [this.state.groups, this.state.keyOwner] = await Promise.all([
          fetchGroupData(this.user, this.parentGroupId),
          userManagement.getKeyOwner(this.parentGroupId)
        ]);
      } else {
        this.state.groups = await this.getAllOrgGroups();
      }
      const ownerIds = this.state.groups.map((group) => group.metadata.ownerId);
      // If we are in a subgroup list, the owners are necessarily
      // part of the parent group, so we fetch from there
      let owners = [];
      if (this.parentGroupId) {
        owners = (await userManagement.get(
          `/extension/groups/${this.parentGroupId}/members`
        )).data.users;
      } else if (isAdmin) {
        // Split fetching users into smaller chunks to avoid exceeding query string limit
        const ownerChunks = chunk(ownerIds, 50);
        const responses = await Promise.all(
          ownerChunks.map(async chunk => {
            const idList = chunk.map((id) => `"${id}"`).join(',');
            return await userManagement.get('/users', {
              q: `user_id:(${idList})`,
            });
          })
        );
        for (let i = 0; i < responses.length; i++) {
          owners = concat(owners, responses[i].data);
        }
      }
      const ownersById = keyBy(owners, 'user_id');
      this.state.groups = this.state.groups.map((group, index) => ({
        ...group,
        owner: ownersById[group.metadata.ownerId],
      }));
      this.state.allGroups = cloneDeep(this.state.groups);
      this.updateFuse();
      if (this.parentGroupId) {
        const org = this.user.groupsData && this.user.groupsData[0];
        this.$analytics.event('Org Management', 'org_groups_loaded', org ? org.name : null, this.state.groups.length);
      }
    } catch (e) {
      console.error(e);
      this.state.error = e;
    } finally {
      this.state.loading = false;
    }
  },
  methods: {
    async getAllNestedGroups() {
      return (await userManagement.get(
        `/extension/groups/${this.parentGroupId}/nested`
      )).data.map((group) => ({
        ...group,
        metadata: parseGroupMetadata(group),
      }));
    },
    async getMyGroups() {
      let groups = (await userManagement.get(
        `/extension/users/${this.user.id}/groups`
      )).data.map((group) => ({
        ...group,
        metadata: parseGroupMetadata(group),
      }));
      // If we are displaying subgroups, filter out groups without parent
      groups = groups.filter((group) => group.metadata.parent);
      await Promise.all(
        groups.map(async (group) => {
          const response = await userManagement.get(
            `/extension/groups/${group._id}/members`,
            {
              page: 999,
            }
          );
          group.members = new Array(response.data.total);
        })
      );
      return groups;
    },
    async getAllOrgGroups() {
      return (await userManagement.get('/extension/groups')).data.groups
        .map((group) => ({
          ...group,
          metadata: parseGroupMetadata(group),
        }))
        .filter((group) => !group.metadata.parent);
    },
    updateFuse() {
      this.fuse = new Fuse(
        this.state.allGroups.map((group) => ({
          ...group,
          name: stripPrefix(group.name),
        })),
        {
          keys: [
            'name',
            'owner.email',
            'owner.given_name',
            'owner.family_name',
          ],
          threshold: 0.2,
          distance: 1000,
        }
      );
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    inviting() {
      return this.state.inviting;
    },
    updating() {
      return this.state.updating;
    },
    removing() {
      return this.state.removing;
    },
    groups() {
      return this.state.groups;
    },
    error() {
      return this.state.error;
    },
    keyOwner() {
      return this.state.keyOwner;
    },
  },
  listeners: {
    async update(group, updates) {
      this.state.error = null;
      const updatedGroup = merge({}, group, updates);
      try {
        this.state.updating = true;
        await userManagement.patch(`/extension/groups/${group._id}`, updates);
        const index = this.state.groups.findIndex(
          (group) => group._id === group._id
        );
        this.$set(this.state.groups, index, updatedGroup);
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.updating = false;
      }
    },
    async invite(userArg, groupParam) {
      const group = cloneDeep(groupParam);
      this.state.error = null;
      if (!userArg) {
        group.metadata.ownerId = this.user.id;
      }
      if (this.parentGroupId) {
        group.name = `${this.parentGroupId.substr(0, 4)}::${group.name}`;
        group.metadata.parent = this.parentGroupId;
        delete group.metadata.seats;
      }
      const adaptedGroup = {
        ...omit(group, 'metadata'),
        description: JSON.stringify(group.metadata),
      };
      try {
        this.state.inviting = true;
        let owner, createdGroup;
        if (this.parentGroupId) {
          const createGroupResponse = await userManagement.post(
            '/extension/groups',
            adaptedGroup
          );
          owner = this.user;
          createdGroup = createGroupResponse.data;
          await auth.renewSession();
        } else {
          const response = await userManagement.post('/invitations/invite', {
            users: [{
              email: userArg.email,
              role: 'ADMIN',
            }],
            org: adaptedGroup,
          });
          owner = response.data[0].user;
          createdGroup = response.data[0].createdOrg;
        }
        const adaptedCreatedGroup = {
          ...createdGroup,
          metadata: JSON.parse(createdGroup.description),
          owner,
          members: [owner.user_id],
        };
        this.state.groups.push(adaptedCreatedGroup);
        this.state.allGroups.push(adaptedCreatedGroup);
        this.$router.push({
          name: this.parentGroupId ? 'group-details' : 'organization-details',
          params: { orgId: adaptedCreatedGroup._id },
        });
        this.updateFuse();
      } catch (e) {
        console.error(e);
        if (e.message && this.parentGroupId) {
          // If the error includes the group name, replace the namespaced
          // group name with the displayed group name
          e.message = e.message.replace(group.name, groupParam.name);
        }
        this.state.error = e;
      } finally {
        this.state.inviting = false;
      }
    },
    async search(term) {
      if (!term) {
        this.state.groups = this.state.allGroups;
        return;
      }
      try {
        this.state.loading = true;
        if (!this.fuse) {
          return;
        }
        this.state.groups = this.fuse.search(term).map((result) => result.item);
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async remove(groups) {
      this.state.error = null;
      const groupIds = groups.map((group) => group._id);
      try {
        this.state.removing = true;
        if (this.parentGroupId) {
          await userManagement.delete(
            `/extension/groups/${this.parentGroupId}/nested`,
            {},
            { data: groupIds }
          );
        }
        await Promise.all(
          groups.map(async (group) => {
            await userManagement.delete(`/extension/groups/${group._id}`);
          })
        );
        this.state.groups = this.state.groups.filter(
          (g) => !groupIds.includes(g._id)
        );
        this.state.allGroups = this.state.allGroups.filter(
          (g) => !groupIds.includes(g._id)
        );
        this.updateFuse();
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.removing = false;
      }
    },
    clearError() {
      this.state.error = null;
    },
  },
};

const GroupsListContainer = createHOC(GroupsList, options, renderWith);
export default withUser()(GroupsListContainer);
